import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import axios from 'axios';
import { buildPackageUrl } from '../../utils/slugs';
import { buildApiUrl } from '../../config/api';

const AdminPackages = () => {
    const [packages, setPackages] = useState([]);
    const [filteredPackages, setFilteredPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    
    // Search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        title: '',
        duration: '',
        destinations: '',
        price: '',
        original_price: '',
        discount: '',
        rating: 0,
        reviews: 0,
        trending: false,
        partial_enable: false,
        package_category: 'general',
        min_pax: '',
        type: 'featured',
        status: 'active',
        highlights: [],
        inclusions: [],
        exclusions: [],
        itinerary: [],
        stay_plan: []
    });

    // Input states for dynamic fields
    const [highlightInput, setHighlightInput] = useState('');
    const [inclusionInput, setInclusionInput] = useState('');
    const [exclusionInput, setExclusionInput] = useState('');
    
    // Itinerary form
    const [itineraryForm, setItineraryForm] = useState({
        day_number: '',
        title: '',
        description: '',
        meals: '',
        activities: ''
    });

    // Stay Plan form
    const [stayPlanForm, setStayPlanForm] = useState({
        day_number: '',
        location: '',
        stay_type: 'hotel',
        property_name: '',
        room_type: '',
        amenities: '',
        description: ''
    });

    // Image upload
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewUrls, setPreviewUrls] = useState([]);

    // ✅ Get token with validation
    const getToken = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('❌ No token found');
            alert('Session expired. Please login again.');
            window.location.href = '/admin/login';
            return null;
        }
        console.log('🔑 Token retrieved:', token.substring(0, 30) + '...');
        return token;
    };

    // ✅ Check token on mount
    // Filter packages function
    const filterPackages = useCallback(() => {
        let filtered = [...packages];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(pkg => 
                pkg.title?.toLowerCase().includes(term) ||
                pkg.destinations?.toLowerCase().includes(term)
            );
        }

        if (typeFilter !== 'all') {
            filtered = filtered.filter(pkg => pkg.type === typeFilter);
        }

        if (categoryFilter !== 'all') {
            filtered = filtered.filter(pkg => String(pkg.package_category || 'general').toLowerCase() === categoryFilter);
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(pkg => pkg.status === statusFilter);
        }

        setFilteredPackages(filtered);
    }, [packages, searchTerm, typeFilter, categoryFilter, statusFilter]);

    useEffect(() => {
        filterPackages();
    }, [filterPackages]);

    // Fetch all packages
    const fetchPackages = useCallback(async () => {
        try {
            setLoading(true);
            const token = getToken();
            if (!token) return;
            
            const response = await axios.get(buildApiUrl('/packages'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token,
                    'Content-Type': 'application/json'
                }
            });
            console.log('📦 Fetched packages:', response.data);
            
            // Handle different response structures
            let packagesData = [];
            if (response.data.success && response.data.data) {
                packagesData = response.data.data;
            } else if (Array.isArray(response.data)) {
                packagesData = response.data;
            } else if (response.data.packages) {
                packagesData = response.data.packages;
            }
            
            setPackages(packagesData);
            setFilteredPackages(packagesData);
        } catch (error) {
            console.error('Error fetching packages:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert('Failed to fetch packages');
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        console.log('🔍 Token on mount:', token ? 'Present' : 'Missing');
        
        if (!token) {
            alert('Please login to access admin panel');
            window.location.href = '/admin/login';
            return;
        }
        
        fetchPackages();
    }, [fetchPackages]);

    const packageTypes = [
        { value: 'all', label: 'All Types' },
        { value: 'featured', label: 'Featured' },
        { value: 'popular', label: 'Popular' },
        { value: 'premium', label: 'Premium' },
        { value: 'family', label: 'Family' },
        { value: 'honeymoon', label: 'Honeymoon' },
        { value: 'adventure', label: 'Adventure' },
        { value: 'luxury', label: 'Luxury' },
        { value: 'budget', label: 'Budget' }
    ];

    const packageCategories = [
        { value: 'all', label: 'All Categories' },
        { value: 'general', label: 'General Package' },
        { value: 'group', label: 'Group Package' },
        { value: 'corporate', label: 'Corporate Package' },
        { value: 'family', label: 'Family Package' },
        { value: 'honeymoon', label: 'Honeymoon Package' },
        { value: 'adventure', label: 'Adventure Package' }
    ];

    const stayTypes = [
        { value: 'hotel', label: 'Hotel' },
        { value: 'houseboat', label: 'Houseboat' },
        { value: 'resort', label: 'Resort' },
        { value: 'camp', label: 'Camp' },
        { value: 'homestay', label: 'Homestay' }
    ];

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Highlights handlers
    const addHighlight = () => {
        if (highlightInput.trim()) {
            setFormData({
                ...formData,
                highlights: [...formData.highlights, highlightInput.trim()]
            });
            setHighlightInput('');
        }
    };

    const removeHighlight = (index) => {
        const newHighlights = formData.highlights.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            highlights: newHighlights
        });
    };

    // Inclusions handlers
    const addInclusion = () => {
        if (inclusionInput.trim()) {
            setFormData({
                ...formData,
                inclusions: [...formData.inclusions, inclusionInput.trim()]
            });
            setInclusionInput('');
        }
    };

    const removeInclusion = (index) => {
        const newInclusions = formData.inclusions.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            inclusions: newInclusions
        });
    };

    // Exclusions handlers
    const addExclusion = () => {
        if (exclusionInput.trim()) {
            setFormData({
                ...formData,
                exclusions: [...formData.exclusions, exclusionInput.trim()]
            });
            setExclusionInput('');
        }
    };

    const removeExclusion = (index) => {
        const newExclusions = formData.exclusions.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            exclusions: newExclusions
        });
    };

    // Itinerary handlers
    const handleItineraryChange = (e) => {
        const { name, value } = e.target;
        setItineraryForm({
            ...itineraryForm,
            [name]: value
        });
    };

    const addItinerary = () => {
        if (!itineraryForm.day_number || !itineraryForm.title || !itineraryForm.description) {
            alert('Please fill in day number, title and description');
            return;
        }

        const dayNumber = parseInt(itineraryForm.day_number, 10);
        
        if (isNaN(dayNumber) || dayNumber < 1) {
            alert('Please enter a valid day number (positive integer)');
            return;
        }

        const newItinerary = {
            day_number: dayNumber,
            title: itineraryForm.title.trim(),
            description: itineraryForm.description.trim(),
            meals: itineraryForm.meals || '',
            activities: itineraryForm.activities || ''
        };

        setFormData({
            ...formData,
            itinerary: [...formData.itinerary, newItinerary]
        });

        setItineraryForm({
            day_number: '',
            title: '',
            description: '',
            meals: '',
            activities: ''
        });
    };

    const removeItinerary = (index) => {
        const newItinerary = formData.itinerary.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            itinerary: newItinerary
        });
    };

    // Stay Plan handlers
    const handleStayPlanChange = (e) => {
        const { name, value } = e.target;
        setStayPlanForm({
            ...stayPlanForm,
            [name]: value
        });
    };

    const addStayPlan = () => {
        if (!stayPlanForm.day_number || !stayPlanForm.location || !stayPlanForm.property_name) {
            alert('Please fill in day number, location and property name');
            return;
        }

        const dayNumber = parseInt(stayPlanForm.day_number, 10);
        
        if (isNaN(dayNumber) || dayNumber < 1) {
            alert('Please enter a valid day number (positive integer)');
            return;
        }

        const newStay = {
            day_number: dayNumber,
            location: stayPlanForm.location.trim(),
            stay_type: stayPlanForm.stay_type,
            property_name: stayPlanForm.property_name.trim(),
            room_type: stayPlanForm.room_type || '',
            amenities: stayPlanForm.amenities || '',
            description: stayPlanForm.description || ''
        };

        setFormData({
            ...formData,
            stay_plan: [...(formData.stay_plan || []), newStay]
        });

        setStayPlanForm({
            day_number: '',
            location: '',
            stay_type: 'hotel',
            property_name: '',
            room_type: '',
            amenities: '',
            description: ''
        });
    };

    const removeStayPlan = (index) => {
        const newStayPlan = formData.stay_plan.filter((_, i) => i !== index);
        setFormData({
            ...formData,
            stay_plan: newStayPlan
        });
    };

    // Image handlers
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        
        const validFiles = files.filter(file => file.size <= 10 * 1024 * 1024);
        if (validFiles.length !== files.length) {
            alert('Some files exceed 10MB limit and were skipped');
        }
        
        setSelectedFiles(validFiles);
        const urls = validFiles.map(file => URL.createObjectURL(file));
        setPreviewUrls(urls);
    };

    // Form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title || !formData.duration || !formData.destinations || !formData.price) {
            alert('Please fill in all required fields');
            return;
        }

        setUploading(true);
        
        const token = getToken();
        if (!token) {
            setUploading(false);
            return;
        }
        
        const formDataToSend = new FormData();
        
        Object.keys(formData).forEach(key => {
            if (key === 'itinerary' || key === 'highlights' || key === 'inclusions' || key === 'exclusions' || key === 'stay_plan') {
                if (formData[key].length > 0) {
                    formDataToSend.append(key, JSON.stringify(formData[key]));
                }
            } else if (key === 'trending' || key === 'partial_enable') {
                formDataToSend.append(key, formData[key] ? 'true' : 'false');
            } else if (key === 'min_pax') {
                if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) {
                    formDataToSend.append(key, formData[key]);
                }
            } else if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
                formDataToSend.append(key, formData[key]);
            }
        });

        if (selectedFiles.length > 0) {
            selectedFiles.forEach(file => {
                formDataToSend.append('images', file);
            });
        }

        try {
            const url = selectedPackage
                ? buildApiUrl(`/packages/${selectedPackage.id}/update`)
                : buildApiUrl('/packages');
            
            const method = 'post';

            console.log(`🚀 Sending ${method.toUpperCase()} request to:`, url);
            console.log('🔑 Using token:', token.substring(0, 30) + '...');
            
            const response = await axios({
                method,
                url,
                data: formDataToSend,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token
                }
            });

            console.log('✅ Response:', response.data);
            alert(`Package ${selectedPackage ? 'updated' : 'created'} successfully!`);
            setShowModal(false);
            resetForm();
            fetchPackages();
        } catch (error) {
            console.error('❌ Error saving package:', error);
            
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/admin/login';
            } else if (error.response?.status === 403) {
                const serverMessage = error.response?.data?.message || 'Access denied';
                const debug = error.response?.data?.debug;
                const debugText = debug
                    ? `\nDebug: role=${debug.token_role || 'n/a'}, email=${debug.token_email || 'n/a'}, db_role=${debug.db_role || 'n/a'}, db_email=${debug.db_email || 'n/a'}, admin_emails_loaded=${debug.admin_emails_loaded ?? 'n/a'}`
                    : '';
                alert(`Admin access denied while saving package: ${serverMessage}. You are still logged in.${debugText}`);
            } else {
                alert(error.response?.data?.message || 'Failed to save package');
            }
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this package?')) return;

        const token = getToken();
        if (!token) return;

        try {
            await axios.delete(buildApiUrl(`/packages/${id}`), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token
                }
            });
            alert('Package deleted successfully!');
            fetchPackages();
        } catch (error) {
            console.error('Error deleting package:', error);
            if (error.response?.status === 401) {
                alert('Session expired. Please login again.');
                localStorage.removeItem('token');
                window.location.href = '/admin/login';
            } else {
                alert('Failed to delete package');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            duration: '',
            destinations: '',
            price: '',
            original_price: '',
            discount: '',
            rating: 0,
            reviews: 0,
            trending: false,
            partial_enable: false,
            package_category: 'general',
            min_pax: '',
            type: 'featured',
            status: 'active',
            highlights: [],
            inclusions: [],
            exclusions: [],
            itinerary: [],
            stay_plan: []
        });
        setSelectedFiles([]);
        setPreviewUrls([]);
        setSelectedPackage(null);
        setActiveTab('basic');
        setHighlightInput('');
        setInclusionInput('');
        setExclusionInput('');
        setItineraryForm({
            day_number: '',
            title: '',
            description: '',
            meals: '',
            activities: ''
        });
        setStayPlanForm({
            day_number: '',
            location: '',
            stay_type: 'hotel',
            property_name: '',
            room_type: '',
            amenities: '',
            description: ''
        });
    };

    const openEditModal = async (pkg) => {
        console.log('========== EDIT MODAL OPENED ==========');
        console.log('📦 Package ID:', pkg?.id);
        console.log('📦 Package Title:', pkg?.title);

        const token = getToken();
        if (!token) return;

        let packageDetails = pkg;

        try {
            const response = await axios.get(buildApiUrl(`/packages/${pkg.id}`), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-auth-token': token
                }
            });
            packageDetails = response.data || pkg;
        } catch (error) {
            console.error(`Error fetching details for package ${pkg.id}:`, error);
        }

        const itinerary = Array.isArray(packageDetails?.itinerary) ? packageDetails.itinerary : [];
        const stayPlan = Array.isArray(packageDetails?.stay_plan) ? packageDetails.stay_plan : [];
        
        console.log('✅ Processed itinerary:', itinerary);
        console.log('✅ Processed stay plan:', stayPlan);

        setSelectedPackage(packageDetails);
        setFormData({
            title: packageDetails.title || '',
            duration: packageDetails.duration || '',
            destinations: packageDetails.destinations || '',
            price: packageDetails.price || '',
            original_price: packageDetails.original_price || '',
            discount: packageDetails.discount || '',
            rating: packageDetails.rating || 0,
            reviews: packageDetails.reviews || 0,
            trending: packageDetails.trending || false,
            partial_enable: packageDetails.partial_enable === 1 || packageDetails.partial_enable === true || packageDetails.partial_enable === '1',
            package_category: packageDetails.package_category || 'general',
            min_pax: packageDetails.min_pax || '',
            type: packageDetails.type || 'featured',
            status: packageDetails.status || 'active',
            highlights: packageDetails.highlights || [],
            inclusions: packageDetails.inclusions || [],
            exclusions: packageDetails.exclusions || [],
            itinerary: itinerary,
            stay_plan: stayPlan
        });
        
        setShowModal(true);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setTypeFilter('all');
        setStatusFilter('all');
    };

    const getImageUrl = (imagePath) => {
        if (!imagePath) return 'https://placehold.co/50x50/1a1a1a/ffffff?text=No+Img';
        if (imagePath.startsWith('http')) return imagePath;
        return buildApiUrl(`/uploads/${imagePath}`).replace('/api/uploads/', '/uploads/');
    };

    const formatCategoryLabel = (value) => {
        const normalized = String(value || 'general').toLowerCase();
        const match = packageCategories.find((category) => category.value === normalized);
        return match ? match.label : 'General Package';
    };

    // Debug log
    console.log('📍 Token check:', localStorage.getItem('token') ? '✅ Token exists' : '❌ No token');

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main">
                <AdminHeader 
                    title="Manage Packages" 
                    subtitle="Add, edit, or remove tour packages"
                />

                {/* Search and Filter Section */}
                <div className="filters-card">
                    <div className="filters-header">
                        <h3>
                            <i className="fas fa-search"></i>
                            Search & Filter Packages
                        </h3>
                        <button 
                            className="toggle-filters"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </button>
                    </div>

                    {showFilters && (
                        <div className="filters-body">
                            <div className="filters-grid">
                                <div className="filter-group">
                                    <label>Search</label>
                                    <input
                                        type="text"
                                        placeholder="Search by title or destinations..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="filter-group">
                                    <label>Package Type</label>
                                    <select
                                        value={typeFilter}
                                        onChange={(e) => setTypeFilter(e.target.value)}
                                    >
                                        {packageTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Package Category</label>
                                    <select
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                    >
                                        {packageCategories.map(category => (
                                            <option key={category.value} value={category.value}>
                                                {category.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label>Status</label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                    >
                                        <option value="all">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                                <div className="filter-actions">
                                    <button className="reset-btn" onClick={clearFilters}>
                                        <i className="fas fa-undo-alt"></i> Clear
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Add Package Button */}
                <div className="content-header">
                    <div className="results-info">
                        <i className="fas fa-box"></i>
                        <span>Showing <strong>{filteredPackages.length}</strong> of <strong>{packages.length}</strong> packages</span>
                    </div>
                    <button className="add-package-btn" onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}>
                        <i className="fas fa-plus"></i> Add Package
                    </button>
                </div>

                {/* Packages Table */}
                <div className="admin-content">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading packages...</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Image</th>
                                        <th>Title</th>
                                        <th>Duration</th>
                                        <th>Price</th>
                                        <th>Type</th>
                                        <th>Category</th>
                                        <th>Rating</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                        </tr>
                                    </thead>
                                <tbody>
                                    {filteredPackages.length > 0 ? (
                                        filteredPackages.map(pkg => (
                                            <tr key={pkg.id}>
                                                <td className="image-cell">
                                                    <img 
                                                        src={getImageUrl(pkg.image)} 
                                                        alt={pkg.title}
                                                        className="table-thumb"
                                                        onError={(e) => {
                                                            e.target.src = 'https://placehold.co/50x50/1a1a1a/ffffff?text=No+Img';
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <strong>{pkg.title}</strong>
                                                    <br />
                                                    <small className="destinations-text">{pkg.destinations}</small>
                                                </td>
                                                <td>{pkg.duration}</td>
                                                <td className="price-cell">₹{parseFloat(pkg.price).toLocaleString('en-IN')}</td>
                                                <td>
                                                    <span className="type-badge">{pkg.type}</span>
                                                </td>
                                                <td>
                                                    <span className={`type-badge ${String(pkg.package_category || 'general').toLowerCase()}`}>
                                                        {formatCategoryLabel(pkg.package_category)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="rating-badge">
                                                        <i className="fas fa-star"></i> {pkg.rating || 0}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${pkg.status}`}>
                                                        {pkg.status}
                                                    </span>
                                                </td>
                                                <td className="actions-cell">
                                                    <button 
                                                        className="action-btn edit"
                                                        onClick={() => openEditModal(pkg)}
                                                        title="Edit"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button 
                                                        className="action-btn delete"
                                                        onClick={() => handleDelete(pkg.id)}
                                                        title="Delete"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                    <Link 
                                                        to={buildPackageUrl(pkg)}
                                                        className="action-btn view"
                                                        target="_blank"
                                                        title="View"
                                                    >
                                                        <i className="fas fa-eye"></i>
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="empty-state">
                                                <i className="fas fa-box-open"></i>
                                                <p>No packages found</p>
                                                <button className="btn-primary" onClick={() => {
                                                    resetForm();
                                                    setShowModal(true);
                                                }}>
                                                    Add Your First Package
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content large" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{selectedPackage ? 'Edit Package' : 'Add New Package'}</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Tab Navigation */}
                        <div className="modal-tabs">
                            <button 
                                className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
                                onClick={() => setActiveTab('basic')}
                            >
                                <i className="fas fa-info-circle"></i> Basic Info
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'highlights' ? 'active' : ''}`}
                                onClick={() => setActiveTab('highlights')}
                            >
                                <i className="fas fa-star"></i> Highlights
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'inclusions' ? 'active' : ''}`}
                                onClick={() => setActiveTab('inclusions')}
                            >
                                <i className="fas fa-check-circle"></i> Inclusions
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'itinerary' ? 'active' : ''}`}
                                onClick={() => setActiveTab('itinerary')}
                            >
                                <i className="fas fa-map-signals"></i> Itinerary
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'stay' ? 'active' : ''}`}
                                onClick={() => setActiveTab('stay')}
                            >
                                <i className="fas fa-hotel"></i> Stay Plan
                            </button>
                            <button 
                                className={`tab-btn ${activeTab === 'images' ? 'active' : ''}`}
                                onClick={() => setActiveTab('images')}
                            >
                                <i className="fas fa-images"></i> Images
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            {/* All your existing form tabs remain exactly the same */}
                            {/* Basic Info Tab */}
                            {activeTab === 'basic' && (
                                <div className="tab-pane">
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Package Title *</label>
                                            <input type="text" name="title" value={formData.title} onChange={handleInputChange} required placeholder="e.g., Kashmir 5 Nights 6 Days" />
                                        </div>
                                        <div className="form-group">
                                            <label>Duration *</label>
                                            <input type="text" name="duration" value={formData.duration} onChange={handleInputChange} required placeholder="e.g., 5N/6D" />
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Destinations *</label>
                                        <input type="text" name="destinations" value={formData.destinations} onChange={handleInputChange} required placeholder="e.g., Srinagar - Gulmarg - Pahalgam" />
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Price (₹) *</label>
                                            <input type="number" name="price" value={formData.price} onChange={handleInputChange} required placeholder="24999" />
                                        </div>
                                        <div className="form-group">
                                            <label>Original Price (₹)</label>
                                            <input type="number" name="original_price" value={formData.original_price} onChange={handleInputChange} placeholder="29999" />
                                        </div>
                                        <div className="form-group">
                                            <label>Discount (%)</label>
                                            <input type="text" name="discount" value={formData.discount} onChange={handleInputChange} placeholder="17%" />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Package Type</label>
                                            <select name="type" value={formData.type} onChange={handleInputChange}>
                                                {packageTypes.slice(1).map(type => (
                                                    <option key={type.value} value={type.value}>{type.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Package Category</label>
                                            <select name="package_category" value={formData.package_category} onChange={handleInputChange}>
                                                {packageCategories.filter((item) => item.value !== 'all').map(category => (
                                                    <option key={category.value} value={category.value}>{category.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Minimum Pax</label>
                                            <input type="number" min="1" name="min_pax" value={formData.min_pax} onChange={handleInputChange} placeholder="6" />
                                        </div>
                                        <div className="form-group">
                                            <label>Rating</label>
                                            <input type="number" step="0.1" min="0" max="5" name="rating" value={formData.rating} onChange={handleInputChange} />
                                        </div>
                                        <div className="form-group">
                                            <label>Reviews Count</label>
                                            <input type="number" name="reviews" value={formData.reviews} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="form-row">
                                        <div className="form-group checkbox">
                                            <label><input type="checkbox" name="trending" checked={formData.trending} onChange={handleInputChange} /> Mark as Trending</label>
                                        </div>
                                        <div className="form-group checkbox">
                                            <label><input type="checkbox" name="partial_enable" checked={formData.partial_enable} onChange={handleInputChange} /> Enable Custom Partial Payment</label>
                                        </div>
                                        <div className="form-group">
                                            <label>Status</label>
                                            <select name="status" value={formData.status} onChange={handleInputChange}>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Highlights Tab */}
                            {activeTab === 'highlights' && (
                                <div className="tab-pane">
                                    <div className="form-group">
                                        <label>Package Highlights</label>
                                        <div className="input-group">
                                            <input type="text" value={highlightInput} onChange={(e) => setHighlightInput(e.target.value)} placeholder="Add a highlight" />
                                            <button type="button" onClick={addHighlight} className="add-btn">Add</button>
                                        </div>
                                        <div className="tags-list">
                                            {formData.highlights.map((item, index) => (
                                                <span key={index} className="tag">{item}<i className="fas fa-times" onClick={() => removeHighlight(index)}></i></span>
                                            ))}
                                            {formData.highlights.length === 0 && <p className="empty-message">No highlights added yet</p>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Inclusions Tab */}
                            {activeTab === 'inclusions' && (
                                <div className="tab-pane">
                                    <div className="form-group">
                                        <label>Inclusions</label>
                                        <div className="input-group">
                                            <input type="text" value={inclusionInput} onChange={(e) => setInclusionInput(e.target.value)} placeholder="Add an inclusion" />
                                            <button type="button" onClick={addInclusion} className="add-btn">Add</button>
                                        </div>
                                        <div className="tags-list">
                                            {formData.inclusions.map((item, index) => (
                                                <span key={index} className="tag">{item}<i className="fas fa-times" onClick={() => removeInclusion(index)}></i></span>
                                            ))}
                                            {formData.inclusions.length === 0 && <p className="empty-message">No inclusions added yet</p>}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Exclusions</label>
                                        <div className="input-group">
                                            <input type="text" value={exclusionInput} onChange={(e) => setExclusionInput(e.target.value)} placeholder="Add an exclusion" />
                                            <button type="button" onClick={addExclusion} className="add-btn">Add</button>
                                        </div>
                                        <div className="tags-list">
                                            {formData.exclusions.map((item, index) => (
                                                <span key={index} className="tag">{item}<i className="fas fa-times" onClick={() => removeExclusion(index)}></i></span>
                                            ))}
                                            {formData.exclusions.length === 0 && <p className="empty-message">No exclusions added yet</p>}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Itinerary Tab */}
                            {activeTab === 'itinerary' && (
                                <div className="tab-pane">
                                    <div className="itinerary-form">
                                        <h3>Add Day to Itinerary</h3>
                                        <div className="form-row">
                                            <div className="form-group"><label>Day Number *</label><input type="number" name="day_number" value={itineraryForm.day_number} onChange={handleItineraryChange} placeholder="1" min="1" /></div>
                                            <div className="form-group"><label>Day Title *</label><input type="text" name="title" value={itineraryForm.title} onChange={handleItineraryChange} placeholder="e.g., Arrival in Srinagar" /></div>
                                        </div>
                                        <div className="form-group"><label>Description *</label><textarea name="description" value={itineraryForm.description} onChange={handleItineraryChange} rows="3" placeholder="Day description..."></textarea></div>
                                        <div className="form-row">
                                            <div className="form-group"><label>Meals</label><input type="text" name="meals" value={itineraryForm.meals} onChange={handleItineraryChange} placeholder="Breakfast,Dinner" /></div>
                                            <div className="form-group"><label>Activities</label><input type="text" name="activities" value={itineraryForm.activities} onChange={handleItineraryChange} placeholder="Sightseeing,Shikara ride" /></div>
                                        </div>
                                        <button type="button" onClick={addItinerary} className="add-btn">Add to Itinerary</button>
                                    </div>
                                    <div className="itinerary-list">
                                        <h3>Itinerary Days ({formData.itinerary.length})</h3>
                                        {formData.itinerary.length > 0 ? (
                                            <div className="itinerary-items">
                                                {formData.itinerary.map((day, index) => (
                                                    <div key={index} className="itinerary-item-card">
                                                        <div className="itinerary-item-header">
                                                            <span className="day-badge">Day {day.day_number}</span>
                                                            <button type="button" className="remove-btn-small" onClick={() => removeItinerary(index)}><i className="fas fa-times"></i></button>
                                                        </div>
                                                        <h4 className="itinerary-item-title">{day.title}</h4>
                                                        <p className="itinerary-item-description">{day.description}</p>
                                                        {(day.meals || day.activities) && (
                                                            <div className="itinerary-item-meta">
                                                                {day.meals && <span className="meta-tag meals-tag"><i className="fas fa-utensils"></i> {day.meals}</span>}
                                                                {day.activities && <span className="meta-tag activities-tag"><i className="fas fa-hiking"></i> {day.activities}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="empty-message">No itinerary days added yet</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Stay Plan Tab */}
                            {activeTab === 'stay' && (
                                <div className="tab-pane">
                                    <div className="stay-plan-form">
                                        <h3>Add Accommodation</h3>
                                        <div className="form-row">
                                            <div className="form-group"><label>Day Number *</label><input type="number" name="day_number" value={stayPlanForm.day_number} onChange={handleStayPlanChange} placeholder="1" min="1" /></div>
                                            <div className="form-group"><label>Location *</label><input type="text" name="location" value={stayPlanForm.location} onChange={handleStayPlanChange} placeholder="e.g., Srinagar, Gulmarg" /></div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group"><label>Stay Type *</label><select name="stay_type" value={stayPlanForm.stay_type} onChange={handleStayPlanChange}>{stayTypes.map(type => (<option key={type.value} value={type.value}>{type.label}</option>))}</select></div>
                                            <div className="form-group"><label>Property Name *</label><input type="text" name="property_name" value={stayPlanForm.property_name} onChange={handleStayPlanChange} placeholder="e.g., Grand Palace" /></div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group"><label>Room Type</label><input type="text" name="room_type" value={stayPlanForm.room_type} onChange={handleStayPlanChange} placeholder="e.g., Deluxe Room" /></div>
                                            <div className="form-group"><label>Amenities</label><input type="text" name="amenities" value={stayPlanForm.amenities} onChange={handleStayPlanChange} placeholder="WiFi, Breakfast, AC" /></div>
                                        </div>
                                        <div className="form-group"><label>Description</label><textarea name="description" value={stayPlanForm.description} onChange={handleStayPlanChange} rows="2" placeholder="Any special notes..."></textarea></div>
                                        <button type="button" onClick={addStayPlan} className="add-btn">Add to Stay Plan</button>
                                    </div>
                                    <div className="stay-plan-list">
                                        <h3>Accommodation Plan ({formData.stay_plan?.length || 0})</h3>
                                        {formData.stay_plan && formData.stay_plan.length > 0 ? (
                                            <div className="stay-items">
                                                {formData.stay_plan.map((stay, index) => (
                                                    <div key={index} className="stay-item-card">
                                                        <div className="stay-item-header">
                                                            <span className="day-badge">Day {stay.day_number}</span>
                                                            <span className="stay-type-badge">{stay.stay_type}</span>
                                                            <button type="button" className="remove-btn-small" onClick={() => removeStayPlan(index)}><i className="fas fa-times"></i></button>
                                                        </div>
                                                        <h4 className="stay-property-name">{stay.property_name}</h4>
                                                        <p className="stay-location"><i className="fas fa-map-marker-alt"></i> {stay.location}</p>
                                                        {stay.room_type && <p className="stay-room-type"><i className="fas fa-bed"></i> {stay.room_type}</p>}
                                                        {stay.amenities && (
                                                            <div className="stay-amenities">
                                                                {stay.amenities.split(',').map((item, idx) => (
                                                                    <span key={idx} className="amenity-tag">{item.trim()}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {stay.description && <p className="stay-description">{stay.description}</p>}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="empty-message">No accommodation added yet</p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Images Tab */}
                            {activeTab === 'images' && (
                                <div className="tab-pane">
                                    <div className="form-group">
                                        <label>Package Images</label>
                                        <input type="file" multiple accept="image/*" onChange={handleFileChange} />
                                        {previewUrls.length > 0 && (
                                            <div className="image-previews">
                                                {previewUrls.map((url, index) => (
                                                    <img key={index} src={url} alt="Preview" className="preview" />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="submit-btn" disabled={uploading}>
                                    {uploading ? 'Saving...' : (selectedPackage ? 'Update Package' : 'Create Package')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

             <style>{`
                /* AdminPackages.css */

.admin-layout {
    display: flex;
    min-height: 100vh;
    background: #f8fafc;
}

.admin-main {
    flex: 1;
    margin-left: 280px;
    background: #f8fafc;
    padding: 2rem;
}

/* Content Header */
.content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.results-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #64748b;
    font-size: 0.85rem;
}

.results-info i {
    color: #2e7d32;
}

.add-package-btn {
    background: #2e7d32;
    color: white;
    border: none;
    padding: 0.6rem 1.2rem;
    border-radius: 10px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
}

.add-package-btn:hover {
    background: #1b5e20;
    transform: translateY(-1px);
}

/* Filters Card */
.filters-card {
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    margin-bottom: 1.5rem;
    overflow: hidden;
}

.filters-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    cursor: pointer;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.filters-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.filters-title i {
    color: #2e7d32;
}

.filters-title h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.toggle-btn {
    background: none;
    border: none;
    color: #64748b;
    cursor: pointer;
}

.filters-body {
    padding: 1.5rem;
}

.filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
}

.filter-group label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: #475569;
    margin-bottom: 0.25rem;
}

.filter-group input,
.filter-group select {
    width: 100%;
    padding: 0.6rem 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.85rem;
}

.filter-actions {
    display: flex;
    align-items: flex-end;
}

.reset-btn {
    padding: 0.6rem 1rem;
    background: #f1f5f9;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.reset-btn:hover {
    background: #e2e8f0;
}

/* Table */
.table-container {
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    overflow-x: auto;
}

.packages-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 800px;
}

.packages-table th {
    text-align: left;
    padding: 1rem 1.25rem;
    background: #f8fafc;
    font-size: 0.75rem;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    border-bottom: 1px solid #e2e8f0;
}

.packages-table td {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}

.packages-table tr:hover td {
    background: #f8fafc;
}

.image-cell {
    width: 60px;
}

.table-thumb {
    width: 45px;
    height: 45px;
    border-radius: 10px;
    object-fit: cover;
}

.destinations-text {
    font-size: 0.7rem;
    color: #64748b;
}

.price-cell {
    font-weight: 600;
    color: #2e7d32;
}

.type-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    background: #f1f5f9;
    border-radius: 20px;
    font-size: 0.7rem;
    text-transform: capitalize;
}

.rating-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.2rem 0.6rem;
    background: #fef9c3;
    border-radius: 20px;
    font-size: 0.7rem;
    color: #854d0e;
}

.rating-badge i {
    font-size: 0.6rem;
}

.status-badge {
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: capitalize;
}

.status-badge.active {
    background: #dcfce7;
    color: #166534;
}

.status-badge.inactive {
    background: #fee2e2;
    color: #991b1b;
}

.actions-cell {
    white-space: nowrap;
}

.action-btn {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: #f1f5f9;
    color: #64748b;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 0.25rem;
    transition: all 0.2s;
    text-decoration: none;
}

.action-btn:hover {
    transform: translateY(-1px);
}

.action-btn.edit:hover {
    background: #e3f2fd;
    color: #1976d2;
}

.action-btn.delete:hover {
    background: #fee2e2;
    color: #d32f2f;
}

.action-btn.view:hover {
    background: #e8f5e9;
    color: #2e7d32;
}

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    background: white;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #2e7d32;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 3rem !important;
    color: #94a3b8;
}

.empty-state i {
    font-size: 3rem;
    margin-bottom: 1rem;
}

.empty-state p {
    margin-bottom: 1rem;
}

.btn-primary {
    background: #2e7d32;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    cursor: pointer;
}

/* Modal */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-container {
    background: white;
    border-radius: 24px;
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease;
}

.modal-large {
    max-width: 1000px;
}

@keyframes slideUp {
    from { transform: translateY(50px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid #e2e8f0;
    position: sticky;
    top: 0;
    background: white;
    border-radius: 24px 24px 0 0;
}

.modal-header h2 {
    font-size: 1.2rem;
    color: #1e293b;
    margin: 0;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #94a3b8;
    width: 32px;
    height: 32px;
    border-radius: 8px;
}

.close-btn:hover {
    background: #f1f5f9;
}

/* Modal Tabs */
.modal-tabs {
    display: flex;
    gap: 0.5rem;
    padding: 1rem 1.5rem;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    flex-wrap: wrap;
}

.tab-btn {
    padding: 0.5rem 1rem;
    background: none;
    border: none;
    border-radius: 30px;
    font-size: 0.8rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #64748b;
    transition: all 0.2s;
}

.tab-btn i {
    font-size: 0.8rem;
}

.tab-btn:hover {
    background: #e2e8f0;
}

.tab-btn.active {
    background: #2e7d32;
    color: white;
}

/* Modal Form */
.modal-form {
    padding: 1.5rem;
}

.form-scroll {
    max-height: 55vh;
    overflow-y: auto;
    padding-right: 0.5rem;
}

.form-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
    margin-bottom: 1rem;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.8rem;
    font-weight: 500;
    color: #475569;
}

.form-group input,
.form-group select,
.form-group textarea {
    width: 100%;
    padding: 0.6rem 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 0.85rem;
}

.form-group.checkbox {
    display: flex;
    align-items: center;
}

.form-group.checkbox label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    cursor: pointer;
}

/* Input with Button */
.input-with-button {
    display: flex;
    gap: 0.5rem;
}

.input-with-button input {
    flex: 1;
}

.add-item-btn {
    padding: 0.6rem 1rem;
    background: #2e7d32;
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
}

/* Tags */
.tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.tag {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.7rem;
    background: #f1f5f9;
    border-radius: 20px;
    font-size: 0.75rem;
}

.tag i {
    cursor: pointer;
    color: #94a3b8;
}

.tag i:hover {
    color: #d32f2f;
}

.empty-message {
    color: #94a3b8;
    font-size: 0.75rem;
    margin-top: 0.5rem;
}

/* Itinerary & Stay Items */
.itinerary-item, .stay-item {
    background: #f8fafc;
    border-radius: 12px;
    padding: 1rem;
    margin-bottom: 1rem;
}

.itinerary-header, .stay-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.day-badge {
    background: #2e7d32;
    color: white;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    font-size: 0.7rem;
}

.stay-type-badge {
    background: #9f7aea;
    color: white;
    padding: 0.2rem 0.6rem;
    border-radius: 20px;
    font-size: 0.7rem;
}

.remove-btn {
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
}

.remove-btn:hover {
    color: #d32f2f;
}

.itinerary-meta {
    display: flex;
    gap: 1rem;
    margin-top: 0.5rem;
    font-size: 0.7rem;
    color: #64748b;
}

.stay-amenities {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.amenity-tag {
    padding: 0.2rem 0.5rem;
    background: white;
    border-radius: 20px;
    font-size: 0.7rem;
    color: #475569;
}

.stay-description {
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 0.5rem;
}

/* Image Previews */
.image-previews {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.preview-img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}

.add-btn {
    background: #2e7d32;
    color: white;
    border: none;
    padding: 0.6rem 1rem;
    border-radius: 10px;
    cursor: pointer;
    margin-top: 0.5rem;
}

/* Modal Footer */
.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
}

.cancel-btn {
    padding: 0.6rem 1.2rem;
    background: #f1f5f9;
    color: #475569;
    border: none;
    border-radius: 10px;
    cursor: pointer;
}

.submit-btn {
    padding: 0.6rem 1.2rem;
    background: #2e7d32;
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.submit-btn:hover:not(:disabled) {
    background: #1b5e20;
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Responsive */
@media (max-width: 1024px) {
    .admin-main {
        margin-left: 0;
        padding: 1rem;
    }
    
    .form-row {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .modal-tabs {
        overflow-x: auto;
        flex-wrap: nowrap;
    }
    
    .tab-btn {
        white-space: nowrap;
    }
    
    .modal-container {
        width: 95%;
        margin: 1rem;
    }
}
                `}
                </style>
        </div>
    );
};

export default AdminPackages;
